import polyline from "@mapbox/polyline";
import camelcaseKeys from "camelcase-keys";

const BASE_URL = "https://api.mapbox.com/directions/v5/mapbox/driving-traffic/";
const ACCESS_TOKEN = import.meta.env.VITE_RIDEMINDER_MAPBOX_ACCESS_TOKEN;

type MapboxResponse = {
  routes: Array<{
    weight_name: string;
    weight: number;
    duration: number;
    distance: number;
    legs: Array<{
      via_waypoints: Array<unknown>;
      admins: Array<{
        iso_3166_1_alpha3: string;
        iso_3166_1: string;
      }>;
      weight: number;
      duration: number;
      steps: Array<unknown>;
      distance: number;
      summary: string;
    }>;
    geometry: string;
  }>;
  waypoints: Array<{
    distance: number;
    name: string;
    location: Array<number>;
  }>;
  code: string;
  uuid: string;
};

/**
 * Format a departure instant the way Mapbox wants it: local time at the ORIGIN, no zone suffix.
 *
 * Deliberately not `toISOString()`, which converts to UTC — a 9am Sydney pickup would be sent as the
 * previous day's 23:00 and priced against overnight traffic.
 */
const toDepartAt = (departAt: Date): string =>
  `${departAt.getFullYear()}-${pad(departAt.getMonth() + 1)}-${pad(departAt.getDate())}T${pad(departAt.getHours())}:${pad(departAt.getMinutes())}`;

const pad = (n: number): string => String(n).padStart(2, "0");

export const fetchDirections = async (points: number[][], departAt?: Date) => {
  try {
    // Construct the URL
    const pointsString = points.map((point) => point.join(",")).join(";");
    /*
     * `depart_at` makes the duration time-of-day aware. Without it this profile answers "leaving
     * now", which is the wrong number for a booking next Tuesday — measured on one Sydney route,
     * 10.8 min at 3am against 14.0 in evening peak.
     *
     * Mapbox accepts it at any horizon (verified to 90 days) and falls back to typical traffic
     * beyond its live window, so a far-future booking still gets a time-of-day answer rather than
     * an error.
     */
    const departParam = departAt ? `&depart_at=${toDepartAt(departAt)}` : "";
    const url = `${BASE_URL}${pointsString}?access_token=${ACCESS_TOKEN}${departParam}`;

    const response = await fetch(url);
    const data = (await response.json()) as MapboxResponse;

    return camelcaseKeys(data);
  } catch (error) {
    console.error("Error fetching directions:", error);
  }
};

/**
 * Resolve a drivable route through the given coordinates (in order): the decoded `{ lat, lng }`
 * points for a Google Maps Polyline, plus the distance and duration Mapbox measured for it.
 *
 * `departAt` makes the duration time-of-day aware; omit it and the duration describes leaving now.
 *
 * Returns an empty route (no path, zero figures) if fewer than two plottable points or the request
 * fails — callers should render neither a line nor an estimate in that case.
 */
export type Route = {
  path: { lat: number; lng: number }[];
  /** Metres and seconds as Mapbox returns them; formatting is the caller's business. */
  distanceMetres: number;
  durationSeconds: number;
};

const EMPTY_ROUTE: Route = { path: [], distanceMetres: 0, durationSeconds: 0 };

export const getRoutePath = async (coords: { lat: number; lng: number }[], departAt?: Date): Promise<Route> => {
  /*
   * Drop points we have no coordinates for before counting. A booking whose address was never
   * validated carries no lat/lng, and interpolating those into the path built
   * ".../driving-traffic/,;," — which Mapbox answers 422 "Latitude must be between -90 and 90".
   *
   * Guarded here as well as at the call site: this is a shared helper, and one bad point should cost
   * the route, not throw a request nobody can service.
   */
  const plottable = coords.filter((c) => Number.isFinite(c?.lat) && Number.isFinite(c?.lng));
  if (plottable.length < 2) return EMPTY_ROUTE;

  const data = await fetchDirections(
    plottable.map((c) => [c.lng, c.lat]),
    departAt
  );
  const route = data?.routes?.[0];
  if (!route?.geometry) return EMPTY_ROUTE;

  return {
    // Mapbox driving geometry is polyline5; @mapbox/polyline decodes to [[lat, lng], ...].
    path: polyline.decode(route.geometry).map(([lat, lng]) => ({ lat, lng })),
    /*
     * Returned rather than discarded: the same response has always carried these, and the booking
     * map needs them for the distance/time badge. The legacy booking page read the same two figures
     * off its own directions response.
     */
    distanceMetres: route.distance ?? 0,
    durationSeconds: route.duration ?? 0,
  };
};
